import { Clock, FileLock2, Plus, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api, DISCLOSURE_FIELDS, type DisclosureCaseSummary, type DisclosureStatus, type NewDisclosureRequest } from '@/api';
import { DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatCount } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, DueCell, EnumBadge, FormActions, FormGrid, overdueFirst, SelectField, useEnumLabel, useEnumOptions } from './ui';
import { DISCLOSURE_REQUESTER_TYPES, DISCLOSURE_STATUS } from './vocab';

type Row = DisclosureCaseSummary;
const OPEN: DisclosureStatus[] = ['RECEIVED', 'UNDER_REVIEW', 'INFO_REQUESTED', 'APPROVED', 'PARTIALLY_APPROVED'];

function RegisterForm({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const options = useEnumOptions();
  const navigate = useNavigate();
  const [f, setF] = useState<NewDisclosureRequest>({
    requester_name: '',
    requester_email: '',
    requester_organization: '',
    requester_type: 'LAW_ENFORCEMENT',
    legal_basis: '',
    purpose: '',
    requested_fields: [],
    domain_name: '',
    urgent: false,
  });
  const write = useWrite((req: NewDisclosureRequest, opts) => api.createDisclosureCase(req, opts));
  const valid = f.requester_name.trim() && f.requester_email.trim() && f.legal_basis.trim() && f.purpose.trim() && f.domain_name.trim() && f.requested_fields.length > 0;
  const toggle = (field: string) =>
    setF({
      ...f,
      requested_fields: f.requested_fields.includes(field) ? f.requested_fields.filter((x) => x !== field) : [...f.requested_fields, field],
    });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const res = await write.run({
      ...f,
      requester_organization: f.requester_organization?.trim() || null,
      domain_name: f.domain_name.trim(),
    });
    if (res) navigate(`/compliance/icann/disclosure/${encodeURIComponent(res.id)}${res.deadline_at ? '' : '?nopolicy=1'}`);
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <FormGrid>
        <Field label={t('disclosure.form.domain')}>
          <Input value={f.domain_name} onChange={(e) => setF({ ...f, domain_name: e.target.value })} disabled={write.busy} spellCheck={false} />
        </Field>
        <SelectField
          label={t('disclosure.form.requesterType')}
          value={f.requester_type}
          options={options('requesterType', DISCLOSURE_REQUESTER_TYPES)}
          onChange={(v) => v && setF({ ...f, requester_type: v })}
          disabled={write.busy}
        />
        <Field label={t('disclosure.form.requesterName')}>
          <Input value={f.requester_name} onChange={(e) => setF({ ...f, requester_name: e.target.value })} disabled={write.busy} />
        </Field>
        <Field label={t('disclosure.form.requesterEmail')}>
          <Input type="email" value={f.requester_email} onChange={(e) => setF({ ...f, requester_email: e.target.value })} disabled={write.busy} />
        </Field>
        <Field label={t('disclosure.form.organization')}>
          <Input value={f.requester_organization ?? ''} onChange={(e) => setF({ ...f, requester_organization: e.target.value })} disabled={write.busy} />
        </Field>
        <Field label={t('disclosure.form.legalBasis')}>
          <Input value={f.legal_basis} onChange={(e) => setF({ ...f, legal_basis: e.target.value })} disabled={write.busy} />
        </Field>
        <Field label={t('disclosure.form.purpose')} className="sm:col-span-2">
          <Textarea rows={2} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} disabled={write.busy} />
        </Field>
      </FormGrid>
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-xs font-medium text-muted-foreground">{t('disclosure.form.fields')}</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {DISCLOSURE_FIELDS.map((field) => (
            // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child
            <label key={field} className="flex items-center gap-2 text-sm">
              <Checkbox checked={f.requested_fields.includes(field)} onCheckedChange={() => toggle(field)} disabled={write.busy} />
              <code className="text-xs">{field}</code>
            </label>
          ))}
        </div>
      </fieldset>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child */}
      <label className="flex items-start gap-2.5 rounded-md border border-critical/30 bg-critical-soft/50 p-3 text-sm">
        <Checkbox checked={Boolean(f.urgent)} onCheckedChange={(v) => setF({ ...f, urgent: v === true })} disabled={write.busy} className="mt-0.5" />
        <span>{t('disclosure.form.urgent')}</span>
      </label>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !valid}>
          {write.busy ? t('disclosure.form.busy') : t('disclosure.form.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

/** `/compliance/icann/disclosure`: registration-data disclosure cases by deadline, overdue first (spec §41). */
export function DisclosurePage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const options = useEnumOptions();
  useDocumentTitle(t('disclosure.list.title'));
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as DisclosureStatus | '';
  const [creating, setCreating] = useState(false);
  const result = useApi((signal) => api.listDisclosureCases({ status: status || undefined, limit: 200 }, { signal }), [status]);
  const items = result.status === 'success' ? overdueFirst(result.data.items, (c) => Boolean(c.overdue)) : [];

  const columns: Column<Row>[] = [
    {
      key: 'case',
      header: t('disclosure.list.col.case'),
      cell: (c) => (
        <Link to={`/compliance/icann/disclosure/${encodeURIComponent(c.id)}`} className="font-mono text-sm font-semibold text-primary hover:underline">
          {c.case_number}
        </Link>
      ),
    },
    {
      key: 'domain',
      header: t('disclosure.list.col.domain'),
      cell: (c) =>
        c.domain_id ? (
          <EntityRef label={c.domain_name} to={`/domains/${encodeURIComponent(c.domain_name)}`} id={c.domain_id} />
        ) : (
          <span title={t('common.notManaged')}>{c.domain_name}</span>
        ),
    },
    {
      key: 'requester',
      header: t('disclosure.list.col.requester'),
      cell: (c) => (
        <>
          <span title={c.requester_type}>{label('requesterType', c.requester_type)}</span>
          {c.requester_organization ? <div className="text-xs text-muted-foreground">{c.requester_organization}</div> : null}
        </>
      ),
    },
    {
      key: 'status',
      header: t('disclosure.list.col.status'),
      cell: (c) => <EnumBadge group="disclosureStatus" value={c.status} tones={DISCLOSURE_STATUS} />,
    },
    {
      key: 'fields',
      header: t('disclosure.list.col.fields'),
      numeric: true,
      cell: (c) => c.requested_fields.length,
    },
    {
      key: 'deadline',
      header: t('disclosure.list.col.deadline'),
      cell: (c) =>
        c.deadline_at ? (
          <DueCell dueAt={c.deadline_at} overdue={Boolean(c.overdue)} done={OPEN.includes(c.status) ? undefined : label('disclosureStatus', c.status)} />
        ) : (
          <DueCell dueAt={null} overdue={false} policyMissing />
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('disclosure.list.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.icann') }]}
        subtitle={t('disclosure.list.subtitle')}
        actions={
          <>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus /> {t('disclosure.list.register')}
            </Button>
            <Button size="sm" variant="outline" onClick={result.reload}>
              <RefreshCw /> {tc('action.refresh')}
            </Button>
          </>
        }
      />
      <ActionDialog open={creating} onOpenChange={setCreating} title={t('disclosure.form.title')} description={t('disclosure.form.description')} wide>
        <RegisterForm onCancel={() => setCreating(false)} />
      </ActionDialog>
      <div className="space-y-6">
        {result.status === 'success' ? (
          <StatGrid className="xl:grid-cols-3">
            <StatCard label={t('disclosure.list.stats.open')} value={formatCount(items.filter((c) => OPEN.includes(c.status)).length)} icon={FileLock2} />
            <StatCard
              label={t('disclosure.list.stats.overdue')}
              value={formatCount(items.filter((c) => c.overdue).length)}
              tone={items.some((c) => c.overdue) ? 'crit' : 'ok'}
              status={items.some((c) => c.overdue) ? t('sla.overdue') : undefined}
              icon={Clock}
            />
            <StatCard
              label={t('disclosure.list.stats.toRelease')}
              value={formatCount(items.filter((c) => c.status === 'APPROVED' || c.status === 'PARTIALLY_APPROVED').length)}
              tone={items.some((c) => c.status === 'APPROVED' || c.status === 'PARTIALLY_APPROVED') ? 'pending' : 'neutral'}
              icon={Send}
            />
          </StatGrid>
        ) : null}
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              status ? (
                <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('common.status')}
              value={status}
              allLabel={t('common.any')}
              options={options('disclosureStatus', Object.keys(DISCLOSURE_STATUS) as DisclosureStatus[])}
              onChange={(v) => {
                const p = new URLSearchParams(params);
                if (v) p.set('status', v);
                else p.delete('status');
                setParams(p);
              }}
              className="w-full sm:w-56"
            />
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={4} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('disclosure.list.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? <DataTable columns={columns} rows={items} rowKey={(c) => c.id} empty={<EmptyState title={t('disclosure.list.empty')} />} /> : null}
        </Section>
      </div>
    </>
  );
}
